// trigger vercel build: no-op comment
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import Dashboard from './pages/Dashboard';
import Explore from './pages/Explore';
import Marketplace from './pages/Marketplace';
import ProductDetailPage from './pages/ProductDetailPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import Layout from './components/Layout';
import ArtworkUpload from './components/ArtworkUpload';
import ProfileEdit from './pages/ProfileEdit';
import Account from './pages/Account';
import AdminDashboard from './pages/AdminDashboard';
import Mentorship from './pages/Mentorship';
import ShopifyCallback from './components/ShopifyCallback';
import { Toaster } from 'react-hot-toast';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { initMetaPixel, trackPageView } from './analytics/metaPixel';
import EditArtworkWrapper from './components/EditArtworkWrapper';
import ShippingPage from './pages/ShippingPage';
import ShipArtworkPage from './pages/ShipArtworkPage';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import './styles/google-places.css';
import PropTypes from 'prop-types';
import FeedbackWidget from './components/FeedbackWidget';
import DialogScrollRail from './components/DialogScrollRail';
import ArtistLanding from './pages/ArtistLanding';
import ExhibitionPage from './pages/ExhibitionPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired
};

// Public Layout Component
const PublicLayout = ({ children }) => {
  return <Layout isPublic>{children}</Layout>;
};

PublicLayout.propTypes = {
  children: PropTypes.node.isRequired
};

const App = () => {
  // Track SPA route changes
  const LocationTracker = () => {
    const location = useLocation();
    useEffect(() => {
      initMetaPixel();
      trackPageView({ page_path: location.pathname + location.search });
    }, [location.pathname, location.search]);
    return null;
  };

  return (
    <>
      <Toaster position="top-center" />
      <DialogScrollRail />
      <AuthProvider>
        <Router>
          <LocationTracker />
          <Routes>
            {/* Root route - Dashboard for authenticated users */}
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Auth Routes - Direct paths without AUTH_PATH prefix */}
            <Route 
              path="/login" 
              element={<Login />} 
            />
            <Route 
              path="/signup" 
              element={<Signup />} 
            />
            <Route 
              path="/forgot-password" 
              element={<ForgotPassword />} 
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload"
              element={
                <ProtectedRoute>
                  <ArtworkUpload />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfileEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/explore"
              element={
                <ProtectedRoute>
                  <Explore />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace"
              element={
                <PublicLayout>
                  <Marketplace />
                </PublicLayout>
              }
            />
            <Route
              path="/marketplace/:handle"
              element={
                <PublicLayout>
                  <ProductDetailPage />
                </PublicLayout>
              }
            />
            <Route
              path="/shipping"
              element={
                <ProtectedRoute>
                  <ShippingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shipping/ship/:id"
              element={
                <ProtectedRoute>
                  <ShipArtworkPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/artwork/:id/edit"
              element={
                <ProtectedRoute>
                  <EditArtworkWrapper />
                </ProtectedRoute>
              }
            />

            <Route path="/auth/shopify/callback" element={<ShopifyCallback />} />

            {/* Public mentorship route for any subdomain */}
            <Route
              path="/mentorship"
              element={
                <PublicLayout>
                  <Mentorship />
                </PublicLayout>
              }
            />

            <Route
              path="/exhibitions/:slug"
              element={
                <PublicLayout>
                  <ExhibitionPage />
                </PublicLayout>
              }
            />

            {/* Public artist acquisition landing page */}
            <Route
              path="/artists/join"
              element={
                <PublicLayout>
                  <ArtistLanding />
                </PublicLayout>
              }
            />
          </Routes>
          <ToastContainer position="top-right" autoClose={4000} hideProgressBar newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
          <FeedbackWidget 
            onFeedbackSent={(data) => {
              console.log('Feedback sent:', data);
              // You can add analytics tracking here later
            }}
          />
        </Router>
      </AuthProvider>
    </>
  );
};

export default App;
